import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { fetchRegulations, getApiErrorMessage } from "../api";
import ErrorRetry from "../components/ErrorRetry";
import Loading from "../components/Loading";
import {
  REGULATION_CATEGORIES,
  isRegulationCategory,
} from "../components/regulations/categories";
import { useAuth } from "../context/AuthContext";
import { PERM } from "../permissions";

const DOC_DATE = new Intl.DateTimeFormat("ru-RU", {
  day: "2-digit",
  month: "2-digit",
  year: "numeric",
  hour: "2-digit",
  minute: "2-digit",
});

function formatDocDate(value) {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "—";
  return DOC_DATE.format(date);
}

function instructionWord(count) {
  const abs = Math.abs(count) % 100;
  const last = abs % 10;
  if (abs > 10 && abs < 20) return "инструкций";
  if (last === 1) return "инструкция";
  if (last >= 2 && last <= 4) return "инструкции";
  return "инструкций";
}

function ChevronIcon() {
  return (
    <svg
      className="reg-card-chevron"
      width="18"
      height="18"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <polyline points="9 6 15 12 9 18" />
    </svg>
  );
}

export default function Regulations() {
  const { can } = useAuth();
  const [params, setParams] = useSearchParams();
  const canEdit = can(PERM.REGULATIONS_MANAGE);
  const category = isRegulationCategory(params.get("category"))
    ? params.get("category")
    : "job";
  const query = params.get("q") || "";
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const tabRefs = useRef([]);

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      setDocs(await fetchRegulations(category));
    } catch (err) {
      setError(getApiErrorMessage(err, "Не удалось загрузить регламенты"));
    } finally {
      setLoading(false);
    }
  }, [category]);

  useEffect(() => {
    load();
  }, [load]);

  const filtered = useMemo(() => {
    const needle = query.trim().toLowerCase();
    if (!needle) return docs;
    return docs.filter((doc) => String(doc.title || "").toLowerCase().includes(needle));
  }, [docs, query]);

  const setQuery = (value) => {
    setParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (!isRegulationCategory(next.get("category"))) next.set("category", category);
        if (value) next.set("q", value);
        else next.delete("q");
        return next;
      },
      { replace: true }
    );
  };

  const selectCategory = (id, tabIndex) => {
    setParams({ category: id });
    requestAnimationFrame(() => tabRefs.current[tabIndex]?.focus());
  };

  const onTabListKeyDown = (event) => {
    const keys = { ArrowRight: 1, ArrowLeft: -1, Home: "home", End: "end" };
    const action = keys[event.key];
    if (action == null) return;
    event.preventDefault();
    const last = REGULATION_CATEGORIES.length - 1;
    const current = REGULATION_CATEGORIES.findIndex((item) => item.id === category);
    let next = current < 0 ? 0 : current;
    if (action === "home") next = 0;
    else if (action === "end") next = last;
    else next = (current + action + REGULATION_CATEGORIES.length) % REGULATION_CATEGORIES.length;
    selectCategory(REGULATION_CATEGORIES[next].id, next);
  };

  const emptyTitle =
    docs.length === 0
      ? "В этой категории пока нет инструкций."
      : "Ничего не найдено по запросу.";
  const emptyHint =
    docs.length === 0
      ? canEdit
        ? "Добавьте первую — её увидят все, у кого есть доступ к разделу."
        : "Когда инструкция появится, она откроется в этом списке."
      : "Попробуйте другое название или сбросьте поиск.";

  return (
    <div className="page-container regulations-page">
      <div className="page-header staff-header">
        <div className="reg-header-copy">
          <h2>Регламенты</h2>
          <p className="page-subtitle staff-subtitle">
            Должностные инструкции, бухгалтерские и технические гайды, задачи в ФК, остальные правила компании
          </p>
        </div>
        {canEdit && (
          <div className="staff-header-actions">
            <Link className="staff-add-btn" to={`/regulations/new?category=${category}`}>
              Создать инструкцию
            </Link>
          </div>
        )}
      </div>

      <div
        className="reg-tabs"
        role="tablist"
        aria-label="Категории регламентов"
        onKeyDown={onTabListKeyDown}
      >
        {REGULATION_CATEGORIES.map((item, index) => {
          const selected = category === item.id;
          return (
            <Link
              key={item.id}
              ref={(node) => {
                tabRefs.current[index] = node;
              }}
              role="tab"
              id={`reg-tab-${item.id}`}
              aria-selected={selected}
              aria-controls="reg-panel"
              tabIndex={selected ? 0 : -1}
              className={`reg-tab${selected ? " is-active" : ""}`}
              to={`/regulations?category=${item.id}`}
            >
              {item.label}
            </Link>
          );
        })}
      </div>

      {error && <ErrorRetry message={error} onRetry={load} />}

      {!error && (
        <div className="staff-toolbar">
          <label className="staff-search" htmlFor="reg-search">
            <span className="staff-visually-hidden">Поиск регламентов</span>
            <input
              id="reg-search"
              type="search"
              name="regulation-search"
              autoComplete="off"
              spellCheck={false}
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder="Например, бухгалтер…"
              aria-label="Поиск по названию"
            />
          </label>
          <span className="staff-count" aria-live="polite">
            {query.trim()
              ? `${filtered.length} из ${docs.length}`
              : `${docs.length} ${instructionWord(docs.length)}`}
          </span>
        </div>
      )}

      {loading && <Loading />}

      <div
        className="reg-panel"
        id="reg-panel"
        role="tabpanel"
        aria-labelledby={`reg-tab-${category}`}
      >
        {!loading && !error && filtered.length === 0 && (
          <div className="reg-empty">
            <p className="reg-empty-title">{emptyTitle}</p>
            <p className="reg-empty-hint">{emptyHint}</p>
            {docs.length === 0 && canEdit && (
              <Link className="staff-add-btn" to={`/regulations/new?category=${category}`}>
                Создать инструкцию
              </Link>
            )}
            {docs.length > 0 && query.trim() && (
              <button type="button" className="btn" onClick={() => setQuery("")}>
                Сбросить поиск
              </button>
            )}
          </div>
        )}

        {!loading && !error && filtered.length > 0 && (
          <ul className="reg-list">
            {filtered.map((doc) => (
              <li key={doc.id} className="reg-list-item">
                <Link className="reg-card" to={`/regulations/${doc.id}`}>
                  <span className="reg-card-body">
                    <span className="reg-card-title">{doc.title}</span>
                    <span className="reg-card-meta">
                      <time dateTime={doc.updated_at || undefined}>
                        {formatDocDate(doc.updated_at)}
                      </time>
                      {doc.updated_by_name ? ` · ${doc.updated_by_name}` : ""}
                    </span>
                  </span>
                  <ChevronIcon />
                </Link>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
